/**
 * Yog.js  — MODIFIED: SEO + Analytics
 *
 * Changes from original:
 *  ✓ YogPageMeta     — dynamic title/description/OG/canonical/BreadcrumbList
 *  ✓ VideoSchemaMeta — VideoObject JSON-LD per playing video (rich results)
 *  ✓ useYogAnalytics — full event tracking (category, group, video, search, accordions)
 *  ✓ useYogSearch    — receives analytics instance so search events fire correctly
 *  ✓ All existing logic and UI is 100% unchanged
 *
 * Premium, mobile-first yoga browser.
 * Design mirrors Rogyog exactly — same layout, same interaction patterns,
 * same component structure — keeping the emerald/green palette.
 *
 * Data shape  (yoga.json — 4 levels):
 *   category  →  subcat  →  subSubCat (GROUP)  →  subSubCat (individual videos)
 *
 * Features:
 *  ✓ Search by name / tag / condition  (useYogSearch hook)
 *  ✓ Tag chip filters
 *  ✓ Autoplay on video selection (matches Rogyog behaviour)
 *  ✓ 3-column browse layout:
 *       Col 1 — Groups sidebar (all subcats pooled, each group shows a level chip)
 *       Col 2 — Video list for the selected group
 *       Col 3 — Player + accordions + thumbnail strip (desktop)
 *  ✓ Accordion detail panels (description / instructions / benefits / precautions)
 */

import React, {
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
} from "react";
import Data from "../../../apiData/yoga.json";
import useYogSearch, { YOG_ALL_TAGS } from "./useYogSearch";
import useYogAnalytics from "./useYogAnalytics";
import { YogPageMeta, VideoSchemaMeta } from "./YogSeoMeta";
import {
  // layout
  Section,
  Blob,
  Inner,
  // header
  PageHeader,
  Eyebrow,
  Title,
  Desc,
  BadgeRow,
  BadgeCard,
  BadgeIconWrap,
  BadgeLabel,
  // category pills
  PillRow,
  Pill,
  // inline collapsible search
  SearchToggleRow,
  SearchToggleBtn,
  ActiveFilterBadge,
  SearchCollapsible,
  SearchPanel,
  SearchLabel,
  SearchInputWrap,
  SearchInput,
  ClearBtn,
  TagSuggestions,
  TagChip,
  // search results
  SearchResultsWrap,
  ResultsHeader,
  ResultCount,
  ResultsGrid,
  ResultCard,
  ResultThumb,
  ResultPlayOverlay,
  ResultBody,
  ResultName,
  ResultMeta,
  ResultTags,
  ResultTag,
  NoResults,
  // browse
  BrowseGrid,
  MobileVideoList,
  MobileGroupLabel,
  MobileVideoItem,
  Card,
  CardHead,
  SideItem,
  Badge,
  LevelBadge,
  VideoItem,
  GroupHeader,
  RightPanel,
  // player
  PlayerWrap,
  PlayerMeta,
  VideoTitle,
  VideoTag,
  Duration,
  EmptyState,
  // thumbnails (desktop only)
  DesktopThumbSection,
  Thumb,
  ThumbImg,
  ThumbOverlay,
  ThumbName,
  ThumbDur,
  // accordions
  DetailBox,
  AccordionBtn,
  AccordionBody,
  AccordionInner,
} from "./Yog.styles";

// ─── Constants ─────────────────────────────────────────────────────────────────

const YT_EMBED = "https://www.youtube.com/embed";
const ytThumb = (id) => `https://img.youtube.com/vi/${id}/mqdefault.jpg`;
const splitLines = (str = "") =>
  str
    .split("&&")
    .map((s) => s.trim())
    .filter(Boolean);

// ─── Icon atoms ────────────────────────────────────────────────────────────────

const PlayIcon = () => (
  <svg
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path d="M8 5v14l11-7z" />
  </svg>
);

const SearchIcon = () => (
  <svg
    className="search-icon"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const ChevronIcon = () => (
  <svg
    className="chevron-icon"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

// ── Hero badge / category-pill icons — same atoms as Rogyog.js ──
const PersonIcon = (props) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    width="16"
    height="16"
    {...props}
  >
    <circle cx="12" cy="8" r="4" />
    <path d="M4 21c0-4.4 3.6-8 8-8s8 3.6 8 8" />
  </svg>
);

const LeafIcon = (props) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    width="16"
    height="16"
    {...props}
  >
    <path d="M5 21c8 0 14-6 14-16C9 5 5 11 5 21z" />
    <path d="M5 21c0-5 3-9 8-11" />
  </svg>
);

const HeartIcon = (props) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    width="16"
    height="16"
    {...props}
  >
    <path d="M20.8 8.6c0 4.9-8.8 10.4-8.8 10.4S3.2 13.5 3.2 8.6a4.6 4.6 0 0 1 8.8-1.9 4.6 4.6 0 0 1 8.8 1.9z" />
  </svg>
);

const ShieldIcon = (props) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    width="16"
    height="16"
    {...props}
  >
    <path d="M12 3l8 3v6c0 5-3.4 8.4-8 9-4.6-.6-8-4-8-9V6z" />
  </svg>
);

const PeopleIcon = (props) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    width="16"
    height="16"
    {...props}
  >
    <circle cx="9" cy="8" r="3.2" />
    <path d="M2.5 20c0-3.6 2.9-6.4 6.5-6.4S15.5 16.4 15.5 20" />
    <circle cx="17" cy="9" r="2.6" />
    <path d="M15 13.4c2.6.4 4.5 2.6 4.5 5.3" />
  </svg>
);

// One icon + accent color per category pill, cycled by index — same pattern
// as Rogyog's CATEGORY_PILL_ICONS/CATEGORY_PILL_COLORS.
const CATEGORY_PILL_ICONS = [PersonIcon, ShieldIcon, PeopleIcon];
const CATEGORY_PILL_COLORS = ["#3d6b4f", "#c9822f", "#7c5cbf"];

// ─── Accordion ─────────────────────────────────────────────────────────────────
// Modified: accepts onOpen callback for analytics tracking

const Accordion = ({ label, children, onOpen, videoName }) => {
  const [open, setOpen] = useState(false);
  const id = `yog-acc-${label.replace(/\s+/g, "-").toLowerCase()}`;

  const handleClick = useCallback(() => {
    const willOpen = !open;
    setOpen(willOpen);
    // Fire analytics only on first open (not on collapse)
    if (willOpen && onOpen) {
      onOpen(label, videoName);
    }
  }, [open, onOpen, label, videoName]);

  return (
    <>
      <AccordionBtn
        $open={open}
        onClick={handleClick}
        aria-expanded={open}
        aria-controls={id}
      >
        {label}
        <span className="chevron" aria-hidden="true">
          ▾
        </span>
      </AccordionBtn>
      <AccordionBody $open={open} id={id} role="region">
        <AccordionInner>{children}</AccordionInner>
      </AccordionBody>
    </>
  );
};

// ─── AsanaDetail ───────────────────────────────────────────────────────────────
// Modified: passes onAccordionOpen + videoName down to each Accordion

const AsanaDetail = ({ asana, onAccordionOpen }) => {
  if (!asana) return null;
  const videoName = asana.subCatName ?? asana.name;
  return (
    <DetailBox>
      {asana.description && (
        <Accordion
          label="About this Practice"
          onOpen={onAccordionOpen}
          videoName={videoName}
        >
          <p>{asana.description}</p>
        </Accordion>
      )}
      {asana.instruction && (
        <Accordion
          label="Instructions"
          onOpen={onAccordionOpen}
          videoName={videoName}
        >
          <ol>
            {splitLines(asana.instruction).map((l, i) => (
              <li key={i}>{l}</li>
            ))}
          </ol>
        </Accordion>
      )}
      {asana.benifits && (
        <Accordion
          label="Benefits"
          onOpen={onAccordionOpen}
          videoName={videoName}
        >
          <ul>
            {splitLines(asana.benifits).map((l, i) => (
              <li key={i}>{l}</li>
            ))}
          </ul>
        </Accordion>
      )}
      {asana.concequences && (
        <Accordion
          label="Precautions & Consequences"
          onOpen={onAccordionOpen}
          videoName={videoName}
        >
          <ul>
            {splitLines(asana.concequences).map((l, i) => (
              <li key={i}>{l}</li>
            ))}
          </ul>
        </Accordion>
      )}
    </DetailBox>
  );
};

// ─── CollapsibleSearch ─────────────────────────────────────────────────────────
// Modified: fires trackSearchOpen on toggle, passes position to onSelectVideo

const CollapsibleSearch = ({
  query,
  activeTag,
  isSearching,
  results,
  onSetQuery,
  onTagClick,
  onClear,
  onSelectVideo,
  onSearchOpen,
}) => {
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);

  const filterCount = (query.trim() ? 1 : 0) + (activeTag ? 1 : 0);

  const toggle = useCallback(() => {
    setOpen((p) => {
      const willOpen = !p;
      if (willOpen && onSearchOpen) onSearchOpen();
      return willOpen;
    });
  }, [onSearchOpen]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 60);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  const handleClear = useCallback(() => {
    onClear();
    inputRef.current?.focus();
  }, [onClear]);

  return (
    <div style={{ marginBottom: "var(--sp-6)" }}>
      {/* ── Toggle button row ── */}
      <SearchToggleRow>
        <SearchToggleBtn
          type="button"
          $active={open}
          onClick={toggle}
          aria-expanded={open}
          aria-controls="yog-search-panel"
          aria-label={
            open ? "Collapse search" : "Search yoga by name or condition"
          }
        >
          <span className="toggle-label">
            <SearchIcon />
            <span className="toggle-text">
              Search by Name / Condition / Body Part
            </span>
          </span>
          <ChevronIcon />
        </SearchToggleBtn>

        {!open && filterCount > 0 && (
          <ActiveFilterBadge
            aria-label={`${filterCount} filter${filterCount > 1 ? "s" : ""} active`}
          >
            🔖 {filterCount} filter{filterCount > 1 ? "s" : ""} active
          </ActiveFilterBadge>
        )}
      </SearchToggleRow>

      {/* ── Collapsible body ── */}
      <SearchCollapsible
        $open={open}
        id="yog-search-panel"
        role="region"
        aria-label="Search yoga"
      >
        <SearchPanel>
          <SearchInputWrap>
            <SearchIcon />
            <SearchInput
              ref={inputRef}
              id="yog-search-input"
              type="search"
              placeholder="e.g. Neck, Stress, Pranayam, Meditation…"
              value={query}
              onChange={(e) => onSetQuery(e.target.value)}
              aria-label="Search yoga practice by name or condition"
              autoComplete="off"
              spellCheck="false"
            />
            {isSearching && (
              <ClearBtn
                type="button"
                onClick={handleClear}
                aria-label="Clear search"
              >
                ✕
              </ClearBtn>
            )}
          </SearchInputWrap>

          <TagSuggestions role="group" aria-label="Filter by tag">
            {YOG_ALL_TAGS.filter(Boolean).map((tag) => (
              <TagChip
                key={tag}
                type="button"
                $active={activeTag === tag}
                onClick={() => onTagClick(tag)}
                aria-pressed={activeTag === tag}
              >
                {tag}
              </TagChip>
            ))}
          </TagSuggestions>
        </SearchPanel>
      </SearchCollapsible>

      {/* ── Results always render inline, below the panel ── */}
      {isSearching && (
        <SearchResultsWrap>
          <ResultsHeader>
            <ResultCount>
              {results.length} practice{results.length !== 1 ? "s" : ""} found
              {activeTag
                ? ` for "${activeTag}"`
                : query
                  ? ` for "${query}"`
                  : ""}
            </ResultCount>
          </ResultsHeader>

          {results.length > 0 ? (
            <ResultsGrid>
              {results.map((item, i) => (
                <ResultCard
                  key={item.sN}
                  type="button"
                  $index={i}
                  onClick={() => onSelectVideo(item, i)}
                  aria-label={`Play ${item.name}`}
                >
                  <ResultThumb>
                    <img
                      src={ytThumb(item.videoPath)}
                      alt={item.name}
                      loading="lazy"
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                    />
                    <ResultPlayOverlay>
                      <PlayIcon />
                    </ResultPlayOverlay>
                  </ResultThumb>
                  <ResultBody>
                    <ResultName>{item.name}</ResultName>
                    <ResultMeta>
                      {item.category} › {item.subCatName} › {item.groupName}
                      {item.duration ? ` · ⏱ ${item.duration}` : ""}
                    </ResultMeta>
                    <ResultTags>
                      {item.tags
                        .filter(Boolean)
                        .slice(0, 4)
                        .map((t) => (
                          <ResultTag key={t}>{t}</ResultTag>
                        ))}
                    </ResultTags>
                  </ResultBody>
                </ResultCard>
              ))}
            </ResultsGrid>
          ) : (
            <NoResults>
              <span className="icon">🌿</span>
              <p>
                No practices found.
                <br />
                Try a different condition or tag.
              </p>
            </NoResults>
          )}
        </SearchResultsWrap>
      )}
    </div>
  );
};

// ─── VideoPlayer ───────────────────────────────────────────────────────────────
// Unchanged from original

const VideoPlayer = ({ videoId, activeVideo, playerRef }) => (
  <div ref={playerRef}>
    {videoId ? (
      <>
        <PlayerWrap>
          <iframe
            key={videoId}
            src={`${YT_EMBED}/${videoId}?autoplay=1&rel=0&modestbranding=1`}
            title={activeVideo?.subCatName ?? "Yoga practice"}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </PlayerWrap>
        <PlayerMeta>
          <VideoTitle>{activeVideo?.subCatName}</VideoTitle>
          {activeVideo?.asnVideoDuration && (
            <Duration>⏱ {activeVideo.asnVideoDuration}</Duration>
          )}
          {activeVideo?._level && (
            <LevelBadge $level={activeVideo._level}>
              {activeVideo._level}
            </LevelBadge>
          )}
          {activeVideo?.asnTags && (
            <div
              style={{
                width: "100%",
                display: "flex",
                flexWrap: "wrap",
                gap: "0.35rem",
                marginTop: "0.25rem",
              }}
            >
              {activeVideo.asnTags
                .split(",")
                .map((t) => t.trim())
                .filter(Boolean)
                .map((t) => (
                  <VideoTag key={t}>{t}</VideoTag>
                ))}
            </div>
          )}
        </PlayerMeta>
      </>
    ) : (
      <EmptyState aria-live="polite">
        <span className="icon">🧘</span>
        <span>Select a practice to begin</span>
      </EmptyState>
    )}
  </div>
);

// ─── Compute aggregate stats ───────────────────────────────────────────────────

const computeStats = (data = []) => {
  let videos = 0;
  data.forEach((cat) => {
    (cat.subcat ?? []).forEach((sub) => {
      (sub.subSubCat ?? []).forEach((grp) => {
        videos +=
          (grp.subSubCat ?? []).filter((v) => v.asnVideoPath).length ||
          (grp.asnVideoPath ? 1 : 0);
      });
    });
  });
  return { categories: data.length, practices: videos };
};

// ═══════════════════════════════════════════════════════════════════════════════
// YOG — MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

const Yog = () => {
  const playerRef = useRef(null);
  const stats = useMemo(() => computeStats(Data), []);

  // ── Analytics hook ──
  const analytics = useYogAnalytics();

  // ── Navigation state ──
  const [catIdx, setCatIdx] = useState(0);
  const [selectedGroupKey, setSelectedGroupKey] = useState(null);
  const [videoId, setVideoId] = useState(null);
  const [activeVideoNode, setActiveVideoNode] = useState(null);

  // ── Search hook — pass analytics so search events fire correctly ──
  const {
    query,
    activeTag,
    results,
    isSearching,
    setQuery,
    setActiveTag,
    clearSearch,
  } = useYogSearch(analytics);

  // ── Derived hierarchy ──
  const category = useMemo(() => Data?.[catIdx] ?? null, [catIdx]);

  /**
   * flatGroups — all subSubCat (level-3) groups across every subcat for the
   * active category, each annotated with its parent subcat name as `level`.
   */
  const flatGroups = useMemo(() => {
    if (!category) return [];
    return (category.subcat ?? []).flatMap((sub) =>
      (sub.subSubCat ?? [])
        .filter((grp) => {
          const kids = (grp.subSubCat ?? []).filter((v) => v.asnVideoPath);
          return kids.length > 0 || !!grp.asnVideoPath;
        })
        .map((grp) => ({
          key: `${sub.subCatName}::${grp.sN ?? grp.subCatName}`,
          groupName: grp.subCatName,
          level: sub.subCatName,
          node: grp,
          videos: (() => {
            const kids = (grp.subSubCat ?? []).filter((v) => v.asnVideoPath);
            return kids.length > 0 ? kids : [grp];
          })(),
        })),
    );
  }, [category]);

  const effectiveGroupKey = selectedGroupKey ?? flatGroups[0]?.key ?? null;
  const selectedGroup = useMemo(
    () =>
      flatGroups.find((g) => g.key === effectiveGroupKey) ??
      flatGroups[0] ??
      null,
    [flatGroups, effectiveGroupKey],
  );

  const videos = useMemo(() => selectedGroup?.videos ?? [], [selectedGroup]);

  // ── Handlers ──

  // MODIFIED: fires trackCategorySelect
  const selectCat = useCallback((i) => {
    setCatIdx(i);
    setSelectedGroupKey(null);
    setVideoId(null);
    setActiveVideoNode(null);
    analytics.trackCategorySelect(Data[i]?.category, i);
  }, [analytics]);

  // MODIFIED: fires trackGroupSelect
  const selectGroup = useCallback((groupKey) => {
    setSelectedGroupKey(groupKey);
    setVideoId(null);
    setActiveVideoNode(null);
    const group = flatGroups.find((g) => g.key === groupKey);
    if (group) {
      analytics.trackGroupSelect(group.groupName, group.level, category?.category);
    }
  }, [flatGroups, category, analytics]);

  // MODIFIED: accepts source param for analytics
  const selectVid = useCallback(
    (id, node, source = "sidebar") => {
      const annotated = node
        ? { ...node, _level: node._level ?? selectedGroup?.level }
        : null;
      setVideoId(id);
      setActiveVideoNode(annotated ?? null);
      // FIX: trackVideoPlay before clearSearch so query/activeTag are still
      // available in handleSearchSelect when it calls this function
      analytics.trackVideoPlay(
        id,
        node?.subCatName,
        category?.category,
        node?.asnVideoDuration,
        source,
      );
      clearSearch();
      setTimeout(() => {
        if (playerRef.current) {
          playerRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
          // FIX: fire trackPlayerScroll for mobile scroll metric
          if (window.innerWidth < 768) {
            analytics.trackPlayerScroll(node?.subCatName);
          }
        }
      }, 100);
    },
    [clearSearch, selectedGroup, category, analytics],
  );

  // MODIFIED: passes position index and "search" source to analytics
  const handleSearchSelect = useCallback(
    (item, position = 0) => {
      analytics.trackSearchResultClick(item, query, activeTag, position);
      selectVid(item.videoPath, item.node, "search");
    },
    [selectVid, analytics, query, activeTag],
  );

  // MODIFIED: fires trackThumbnailClick before play
  const handleThumbnailClick = useCallback(
    (videoPath, node, indexInGroup) => {
      analytics.trackThumbnailClick(videoPath, node?.subCatName, indexInGroup);
      selectVid(videoPath, node, "thumbnail");
    },
    [selectVid, analytics],
  );

  // MODIFIED: fires trackMobileVideoClick
  const handleMobileVideoClick = useCallback(
    (videoPath, node) => {
      analytics.trackMobileVideoClick(videoPath, node?.subCatName, selectedGroup?.groupName);
      selectVid(videoPath, node, "mobile_list");
    },
    [selectVid, analytics, selectedGroup],
  );

  // Accordion open handler — passed down to AsanaDetail
  const handleAccordionOpen = useCallback(
    (label, videoName) => {
      analytics.trackAccordionOpen(label, videoName);
    },
    [analytics],
  );

  // Auto-select first video when the group changes
  useEffect(() => {
    if (videos.length > 0) {
      setVideoId(videos[0].asnVideoPath);
      setActiveVideoNode(videos[0]);
    } else {
      setVideoId(null);
      setActiveVideoNode(null);
    }
  }, [selectedGroup]); // eslint-disable-line react-hooks/exhaustive-deps

  const resolvedActiveNode = useMemo(() => {
    const base =
      videos.find((v) => v.asnVideoPath === videoId) ?? activeVideoNode ?? null;
    if (base && !base._level && selectedGroup?.level) {
      return { ...base, _level: selectedGroup.level };
    }
    return base;
  }, [videos, videoId, activeVideoNode, selectedGroup]);

  // ── Flat mobile list (all groups across all subcats) ──
  const mobileGroupedVideos = useMemo(
    () =>
      flatGroups.map((g) => ({
        groupName: g.groupName,
        level: g.level,
        videos: g.videos,
      })),
    [flatGroups],
  );

  return (
    <Section id="yog">
      {/* ── SEO: Dynamic page meta + structured data ── */}
      <YogPageMeta
        categoryName={category?.category}
        groupName={selectedGroup?.groupName}
        videoName={resolvedActiveNode?.subCatName}
        videoDesc={resolvedActiveNode?.description}
      />
      {videoId && (
        <VideoSchemaMeta
          videoId={videoId}
          videoName={resolvedActiveNode?.subCatName}
          description={resolvedActiveNode?.description}
          duration={resolvedActiveNode?.asnVideoDuration}
          category={category?.category}
        />
      )}

      <Blob className="g1" aria-hidden="true" />
      <Blob className="g2" aria-hidden="true" />
      <Blob className="g3" aria-hidden="true" />

      <Inner>
        {/* ── Header ── */}
        <PageHeader>
          <Eyebrow>✦ YOGASHASTRA GALLERY</Eyebrow>
          <Title>Yoga Practices</Title>
          <Desc>
            Explore curated yoga practices for better strength, flexibility, mindfulness, and overall well-being—for body, mind, and spirit.

          </Desc>
        </PageHeader>

        <BadgeRow>
          <BadgeCard>
            <BadgeIconWrap>
              <PersonIcon />
            </BadgeIconWrap>
            <BadgeLabel>Evidence-based Practices</BadgeLabel>
          </BadgeCard>
          <BadgeCard>
            <BadgeIconWrap>
              <LeafIcon />
            </BadgeIconWrap>
            <BadgeLabel>For All Ages &amp; Levels</BadgeLabel>
          </BadgeCard>
          <BadgeCard>
            <BadgeIconWrap>
              <HeartIcon />
            </BadgeIconWrap>
            <BadgeLabel>Mind, Body &amp; Breath</BadgeLabel>
          </BadgeCard>
          <BadgeCard>
            <BadgeIconWrap>
              <ShieldIcon />
            </BadgeIconWrap>
            <BadgeLabel>Safe &amp; Holistic Approach</BadgeLabel>
          </BadgeCard>
        </BadgeRow>

        {/* ── Category pills ── */}
        <PillRow role="tablist" aria-label="Yoga categories">
          {Data?.map((cat, i) => {
            const PillIcon = CATEGORY_PILL_ICONS[i % CATEGORY_PILL_ICONS.length];
            const iconColor = CATEGORY_PILL_COLORS[i % CATEGORY_PILL_COLORS.length];
            return (
              <Pill
                key={cat.sN ?? i}
                type="button"
                role="tab"
                aria-selected={i === catIdx}
                $active={i === catIdx}
                $iconColor={iconColor}
                $index={i}
                onClick={() => selectCat(i)}
              >
                <PillIcon />
                {cat.category}
              </Pill>
            );
          })}
        </PillRow>

        {/* ── Inline collapsible search ── */}
        <CollapsibleSearch
          query={query}
          activeTag={activeTag}
          isSearching={isSearching}
          results={results}
          onSetQuery={setQuery}
          onTagClick={setActiveTag}
          onClear={clearSearch}
          onSelectVideo={handleSearchSelect}
          onSearchOpen={analytics.trackSearchOpen}
        />

        {/* ── Browse mode (hidden while search results are shown) ── */}
        {!isSearching && (
          <>
            <BrowseGrid>
              {/* Col 1 — Groups sidebar */}
              <Card $delay={0} aria-label="Sub-categories">
                <CardHead>Sub-categories</CardHead>
                {flatGroups.length > 0 ? (
                  flatGroups.map((g) => {
                    const isActive = g.key === effectiveGroupKey;
                    return (
                      <SideItem
                        key={g.key}
                        type="button"
                        $active={isActive}
                        onClick={() => selectGroup(g.key)}
                        aria-pressed={isActive}
                      >
                        <span className="side-content">
                          <span className="side-name">{g.groupName}</span>
                          <LevelBadge $level={g.level}>{g.level}</LevelBadge>
                        </span>
                        <Badge $active={isActive}>{g.videos.length}</Badge>
                      </SideItem>
                    );
                  })
                ) : (
                  <SideItem
                    as="div"
                    style={{ opacity: 0.5, cursor: "default" }}
                  >
                    <span className="side-content">
                      <span className="side-name">No sub-categories</span>
                    </span>
                  </SideItem>
                )}
              </Card>

              {/* Col 2 — Video list sidebar */}
              <Card $delay={80} aria-label={`Videos (${videos.length})`}>
                <CardHead>
                  Videos ({videos.length})
                  {selectedGroup && (
                    <LevelBadge
                      $level={selectedGroup.level}
                      style={{ marginLeft: 8, verticalAlign: "middle" }}
                    >
                      {selectedGroup.level}
                    </LevelBadge>
                  )}
                </CardHead>
                {videos.length > 0 ? (
                  videos.map((v) => {
                    const isActive = v.asnVideoPath === videoId;
                    return (
                      <VideoItem
                        key={v.sN ?? v.asnVideoPath}
                        type="button"
                        $active={isActive}
                        onClick={() => selectVid(v.asnVideoPath, v, "sidebar")}
                        aria-pressed={isActive}
                        aria-label={`Play ${v.subCatName}`}
                      >
                        <span className="vid-thumb">
                          <img
                            src={ytThumb(v.asnVideoPath)}
                            alt={v.subCatName}
                            loading="lazy"
                            onError={(e) => {
                              e.currentTarget.style.display = "none";
                            }}
                          />
                          <span className="vid-play">
                            <svg
                              viewBox="0 0 24 24"
                              xmlns="http://www.w3.org/2000/svg"
                              aria-hidden="true"
                            >
                              <path d="M8 5v14l11-7z" />
                            </svg>
                          </span>
                        </span>
                        <span className="vid-info">
                          <span className="video-name">{v.subCatName}</span>
                          {v.asnVideoDuration && (
                            <span className="vid-dur">
                              ⏱ {v.asnVideoDuration}
                            </span>
                          )}
                        </span>
                      </VideoItem>
                    );
                  })
                ) : (
                  <SideItem
                    as="div"
                    style={{ opacity: 0.5, cursor: "default" }}
                  >
                    <span className="side-content">
                      <span className="side-name">No videos</span>
                    </span>
                  </SideItem>
                )}
              </Card>

              {/* Col 3 — Player + thumbs + details */}
              <RightPanel>
                {/* Video player — top on both mobile and desktop */}
                <VideoPlayer
                  videoId={videoId}
                  activeVideo={resolvedActiveNode}
                  playerRef={playerRef}
                />

                {/* Asana detail accordions — right after player on mobile */}
                {/* MODIFIED: passes onAccordionOpen for analytics */}
                <AsanaDetail
                  asana={resolvedActiveNode}
                  onAccordionOpen={handleAccordionOpen}
                />

                {/* Thumbnail grid — desktop only */}
                {/* MODIFIED: uses handleThumbnailClick for analytics */}
                {videos.length > 0 && (
                  <DesktopThumbSection aria-label="Video thumbnails">
                    {videos.map((v, i) => (
                      <Thumb
                        key={v.sN ?? v.asnVideoPath}
                        type="button"
                        $active={v.asnVideoPath === videoId}
                        $index={i}
                        onClick={() => handleThumbnailClick(v.asnVideoPath, v, i)}
                        aria-label={`Play ${v.subCatName}`}
                        aria-pressed={v.asnVideoPath === videoId}
                      >
                        <ThumbImg>
                          <img
                            src={ytThumb(v.asnVideoPath)}
                            alt={v.subCatName}
                            loading="lazy"
                            onError={(e) => {
                              e.currentTarget.style.display = "none";
                            }}
                          />
                          <ThumbOverlay $active={v.asnVideoPath === videoId}>
                            <PlayIcon />
                          </ThumbOverlay>
                        </ThumbImg>
                        <ThumbName>{v.subCatName}</ThumbName>
                        <ThumbDur>{v.asnVideoDuration}</ThumbDur>
                      </Thumb>
                    ))}
                  </DesktopThumbSection>
                )}

                {/* Mobile video list — below player+detail, mobile only */}
                {/* MODIFIED: uses handleMobileVideoClick for analytics */}
                {mobileGroupedVideos.length > 0 && (
                  <MobileVideoList aria-label="Practices">
                    {mobileGroupedVideos.map((section, si) => (
                      <React.Fragment key={si}>
                        {section.groupName && (
                          <MobileGroupLabel>
                            <span style={{ marginRight: 6 }}>
                              {section.groupName}
                            </span>
                            <LevelBadge $level={section.level}>
                              {section.level}
                            </LevelBadge>
                          </MobileGroupLabel>
                        )}
                        {section.videos.map((v) => (
                          <MobileVideoItem
                            key={v.sN ?? v.asnVideoPath}
                            type="button"
                            $active={v.asnVideoPath === videoId}
                            onClick={() => handleMobileVideoClick(v.asnVideoPath, v)}
                            aria-pressed={v.asnVideoPath === videoId}
                            aria-label={`Play ${v.subCatName}`}
                          >
                            <div className="info">
                              <div className="name">{v.subCatName}</div>
                              <div className="dur">⏱ {v.asnVideoDuration}</div>
                            </div>
                            <span className="dot" aria-hidden="true" />
                          </MobileVideoItem>
                        ))}
                      </React.Fragment>
                    ))}
                  </MobileVideoList>
                )}
              </RightPanel>
            </BrowseGrid>
          </>
        )}
      </Inner>
    </Section>
  );
};

Yog.displayName = "Yog";
export default React.memo(Yog);